import { ExportParams, breakdownRows, perfectScans } from './exportWeeklyReport';
import { OpsReportData, CountEntry } from './opsReportData';

function csvEscape(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function csvRow(cells: string[]): string {
  return cells.map(csvEscape).join(',');
}

function downloadCsv(lines: string[], period: string, filePrefix: string): void {
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = period.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'report';
  a.download = `${filePrefix}-${slug}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function aiEfficiencySections(params: ExportParams): string[] {
  const { summary, breakdown, fieldAcc, weekScans } = params;
  const lines: string[] = [];

  lines.push(csvRow(['Summary']));
  lines.push(csvRow(['Metric', 'Value']));
  lines.push(csvRow(['Total Scans Processed', String(summary.total_receipts)]));
  lines.push(csvRow(['Overall Field Accuracy', `${summary.overall_avg_score_pct}%`]));
  lines.push(csvRow(['100% Accurate Scans',
    breakdown ? String(perfectScans(breakdown)) : '—']));
  lines.push(csvRow(['Weakest Field',
    fieldAcc.length > 0 ? `${fieldAcc[0].label} (${fieldAcc[0].correct_pct}%)` : '—']));
  lines.push('');

  if (breakdown) {
    lines.push(csvRow(['Category Breakdown']));
    lines.push(csvRow(['Category', 'Total Scans', 'Avg Accuracy', 'Perfect (100%)', 'Mid (80-99%)', 'Low (<80%)']));
    breakdownRows(breakdown).forEach(r => lines.push(csvRow(r)));
    lines.push('');
  }

  if (fieldAcc.length > 0) {
    lines.push(csvRow(['Field-Level Accuracy']));
    lines.push(csvRow(['Field', 'Accuracy', 'Scans Analyzed']));
    for (const f of fieldAcc) {
      lines.push(csvRow([f.label, `${f.correct_pct}%`, String(f.total)]));
    }
    lines.push('');
  }

  if (weekScans.length > 0) {
    lines.push(csvRow(['Scan Details']));
    lines.push(csvRow(['Scan ID', 'Client', 'Date', 'Accuracy', 'Score', 'Corrected Data']));
    for (const s of weekScans) {
      lines.push(csvRow([
        s.scan_id,
        s.client_name,
        s.date,
        `${s.accuracy_pct}%`,
        `${s.fields_correct}/${s.fields_total}`,
        s.issues.length > 0 ? s.issues.join(', ') : '—',
      ]));
    }
  }

  return lines;
}

function floorControlSection(title: string, colLabel: string, entries: CountEntry[]): string[] {
  if (entries.length === 0) return [];
  const lines: string[] = [csvRow([title]), csvRow([colLabel, 'Count'])];
  for (const e of entries) lines.push(csvRow([e.label, String(e.count)]));
  lines.push('');
  return lines;
}

function floorControlSections(fc: OpsReportData['floorControl']): string[] {
  return [
    csvRow(['Floor Control']),
    '',
    ...floorControlSection('Exceptions by Type', 'Status', fc.exceptionsByStatus),
    ...floorControlSection('Escalations by Team', 'Team', fc.escalationsByTeam),
    ...floorControlSection('Intake Volume by Operator', 'Operator', fc.intakeByOperator),
    ...floorControlSection('Overs by Status', 'Status', fc.oversByStatus),
  ];
}

export async function exportWeeklyReportCsv(params: ExportParams): Promise<void> {
  const lines = [
    csvRow(['Scan Performance Report']),
    csvRow([params.period]),
    '',
    ...aiEfficiencySections(params),
  ];
  downloadCsv(lines, params.period, 'scan-report');
}

export async function exportOpsReportCsv(data: OpsReportData): Promise<void> {
  const lines = [
    csvRow(['Ops Efficiency Report']),
    csvRow([data.period]),
    ...(data.isMock
      ? [csvRow(['SAMPLE DATA — no extraction activity recorded yet; figures below are illustrative, not measured.'])]
      : []),
    '',
    ...aiEfficiencySections(data),
    ...floorControlSections(data.floorControl),
  ];
  downloadCsv(lines, data.period, 'ops-report');
}
